import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import { Edit, Delete, PersonAdd, Person, AdminPanelSettings } from '@mui/icons-material';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface User {
  id: string;
  username: string;
  role: string;
}

const roleStyles: Record<string, { color: string; bgcolor: string; icon: React.ReactNode }> = {
  admin: {
    color: '#fff',
    bgcolor: '#f44336',
    icon: <AdminPanelSettings sx={{ fontSize: 16, mr: 0.5 }} />,
  },
  operator: {
    color: '#fff',
    bgcolor: '#6366f1',
    icon: <Person sx={{ fontSize: 16, mr: 0.5 }} />,
  },
};

const SettingsUsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sheets/users`);
      if (response.ok) {
        const data = await response.json() as { users: User[] };
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, bgcolor: '#f5f5f7', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
          <Person sx={{ color: '#3730f2', fontSize: 32, mr: 1 }} /> User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          sx={{ bgcolor: '#3730f2', borderRadius: 999, fontWeight: 600, px: 3, py: 1, fontSize: 16 }}
        >
          Add User
        </Button>
      </Stack>
      <Paper sx={{ p: 2, borderRadius: 4, mx: 'auto' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    <Box sx={{ width: 300 }}>
                      <Chip
                        icon={roleStyles[user.role]?.icon}
                        label={user.role.toUpperCase()}
                        sx={{
                          bgcolor: roleStyles[user.role]?.bgcolor,
                          color: roleStyles[user.role]?.color,
                          fontWeight: 600,
                          width: '100%',
                          borderRadius: 2,
                          px: 2,
                          fontSize: 14,
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton
                        color="primary"
                        sx={{
                          bgcolor: 'rgba(55,48,242,0.08)',
                          borderRadius: '50%',
                          p: 1,
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: 'rgba(55,48,242,0.18)' },
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        sx={{
                          bgcolor: 'rgba(244,67,54,0.08)',
                          borderRadius: '50%',
                          p: 1,
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: 'rgba(244,67,54,0.18)' },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SettingsUsersPage; 